import { useState, useRef, useEffect, useCallback } from 'react';
import { Building2, ChevronDown, Check, Briefcase } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import {
  useAccessibleTenants,
  accessRoleLabel,
  type AccessibleTenant,
} from '@/api/queries/firm';
import { SwitchTenantDialog, type SwitchTarget } from './SwitchTenantDialog';

// ─────────────────────────────────────────────────────────────
// Sélecteur de dossier (portail cabinet, P1.4)
//
// Monté dans l'en-tête (AppLayout) par l'agent de câblage. N'apparaît que
// si l'utilisateur a accès à PLUS D'UN dossier (tenant d'origine + accès
// accordés via un cabinet). The switch calls POST /auth/switch-tenant,
// replaces the session IN MEMORY (AuthContext.applySwitchedSession, no hard
// reload), clears the whole react-query cache and navigates to « / ».
//
// Un bandeau discret « Dossier cabinet » signale en permanence que l'on
// n'est PAS sur son tenant d'origine (via ≠ HOME).
//
// Choosing a file opens SwitchTenantDialog, which confirms the target by
// name and collects the second factor a client file requires.
// ─────────────────────────────────────────────────────────────

export function TenantSwitcher() {
  const { tenant, isAuthenticated } = useAuth();
  const { data: tenants } = useAccessibleTenants({ enabled: isAuthenticated });

  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<SwitchTarget | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Fermeture au clic extérieur.
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleSwitch = useCallback(
    (entry: AccessibleTenant) => {
      setOpen(false);
      if (entry.tenantId === tenant?.id) return;
      setTarget({ tenantId: entry.tenantId, name: entry.name });
    },
    [tenant?.id]
  );

  // N'affiche rien tant qu'il n'y a pas plus d'un dossier accessible.
  if (!isAuthenticated || !tenants || tenants.length <= 1) {
    return null;
  }

  const currentEntry = tenants.find((t) => t.tenantId === tenant?.id);
  const isFirmSession = currentEntry ? currentEntry.via !== 'HOME' : false;
  const currentName = tenant?.name ?? currentEntry?.name ?? 'Dossier';

  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Changer de dossier"
        className={cn(
          'flex min-w-0 max-w-[16rem] items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
          isFirmSession
            ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100'
            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
        )}
      >
        {isFirmSession ? (
          <Briefcase className="h-4 w-4 flex-shrink-0 text-amber-600" />
        ) : (
          <Building2 className="h-4 w-4 flex-shrink-0 text-slate-400" />
        )}
        <span className="truncate">{currentName}</span>
        {isFirmSession && (
          <span className="hidden flex-shrink-0 rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 sm:inline-block">
            Dossier cabinet
          </span>
        )}
        <ChevronDown className="h-4 w-4 flex-shrink-0 text-slate-400" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 z-50 mt-2 w-72 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
        >
          <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Changer de dossier
          </div>
          <ul className="max-h-80 overflow-y-auto py-1">
            {tenants.map((t) => {
              const isCurrent = t.tenantId === tenant?.id;
              return (
                <li key={t.tenantId}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleSwitch(t)}
                    className={cn(
                      'flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors',
                      isCurrent ? 'bg-brand-lagune-50' : 'hover:bg-slate-50'
                    )}
                  >
                    <span className="flex-shrink-0">
                      {t.via === 'HOME' ? (
                        <Building2 className="h-4 w-4 text-slate-400" />
                      ) : (
                        <Briefcase className="h-4 w-4 text-amber-500" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-slate-900">
                        {t.name}
                      </span>
                      <span className="block truncate text-xs text-slate-500">
                        {t.via === 'HOME'
                          ? 'Votre entreprise'
                          : `Dossier cabinet · ${accessRoleLabel(t.roleCode)}`}
                      </span>
                    </span>
                    {isCurrent ? (
                      <Check className="h-4 w-4 flex-shrink-0 text-brand-lagune-600" />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <SwitchTenantDialog target={target} onClose={() => setTarget(null)} />
    </div>
  );
}

export default TenantSwitcher;
